<script setup lang="ts">
import { LogOut, Plus } from '@lucide/vue'
import { enterpriseBillingAlertLabel } from '@zerohuman/shared'
import {
    Button,
    Separator,
    ZhBackdrop,
    ZhPageTitle,
    ZhPanel,
    ZhStatusPill,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import logo from '@zerohuman/ui/mark.svg'
import { computed, onMounted, ref } from 'vue'
import { api, type EnterpriseAttention, type EnterpriseBillingAlert, type Me, type MeEnterprise } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import EnterpriseAvatar from '@/components/EnterpriseAvatar.vue'
import EnterpriseBillingFlag from '@/components/EnterpriseBillingFlag.vue'
import EnterpriseChips from '@/components/EnterpriseChips.vue'
import EnterpriseCreateForm from '@/components/EnterpriseCreateForm.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'
import { useUiMode } from '@/composables/useUiMode'
import { enterpriseChips, orderEnterprises, pickerLayout, reloadIntoEnterprise } from '@/lib/enterprisePicker'
import { rememberPortalDestination } from '@/lib/portalDestination'

/**
 * Where a person who holds several enterprises says which one this session is for. It stands outside the
 * signed-in shell — there is no enterprise to draw a sidebar for until one is chosen — so it carries its
 * own header, with the one control that still makes sense there: leaving altogether.
 *
 * Each enterprise shows what waits on this person there, read live, and the enterprises that need them most come
 * first. A card slides to its new place when the counts reorder them, and jumps there under reduced motion. An
 * enterprise whose Billing needs them (past due, a limit reached or coming close) carries a flag beside its plan.
 */
const props = defineProps<{ me: Me }>()
const emit = defineEmits<{ logout: [] }>()

const layout = computed(() => pickerLayout((props.me.enterprises ?? []).length))

/**
 * #519: Standard hides each enterprise's slug, on the cards and in the table, and Advanced shows it (#1749). Read
 * from the shared mode, so a mode that arrives after first paint, or a change while the screen is open, follows.
 */
const { mode } = useUiMode()
const showSlug = computed(() => mode.value === 'advanced')

/**
 * Read every 5s while the tab is shown, like the nav's badges. One enterprise is opened at once, so it is never read.
 * A failed read keeps the last counts, and before the first one every enterprise shows its size alone.
 */
const { data: attentionRows } = useLiveResource(
    () => 'enterprise-attention',
    signal => (layout.value === 'single' ? Promise.resolve([]) : api.enterpriseAttention(signal)),
    { everyMs: 5000 },
)
const attention = computed(
    () => new Map<string, EnterpriseAttention>((attentionRows.value ?? []).map(row => [row.enterpriseId, row])),
)
const enterprises = computed<MeEnterprise[]>(() => orderEnterprises(props.me.enterprises ?? [], attention.value))

/**
 * The billing flags, read once a minute: billing moves slowly, and each enterprise's limits cost more to read than
 * its counts. A failed read keeps the last flags; before the first one no enterprise is flagged.
 */
const { data: billingRows } = useLiveResource(
    () => 'enterprise-billing-alerts',
    signal => (layout.value === 'single' ? Promise.resolve([]) : api.enterpriseBillingAlerts(signal)),
    { everyMs: 60_000 },
)
const billing = computed(
    () => new Map<string, EnterpriseBillingAlert>((billingRows.value ?? []).map(row => [row.enterpriseId, row])),
)

/**
 * A card opens from its head, a button named "Open …", and that name hides everything inside it from assistive tech,
 * the flag's own label included. So a flagged card's button carries the flag's reason as its description.
 */
function billingReasonOf(enterpriseId: string): string | null {
    const alert = billing.value.get(enterpriseId)
    return alert ? enterpriseBillingAlertLabel(alert) : null
}
function billingReasonId(enterpriseId: string): string {
    return `billing-reason-${enterpriseId}`
}

function chipsOf(enterprise: Pick<MeEnterprise, 'id' | 'memberCount' | 'teamCount'>) {
    return enterpriseChips(enterprise, attention.value.get(enterprise.id))
}
const busy = ref(false)
const error = ref<string | null>(null)

/** Table rows are flat: the tier's name, not the tier. In the picker's order: the table has no default sort. */
const rows = computed(() =>
    enterprises.value.map(enterprise => ({
        id: enterprise.id,
        name: enterprise.name,
        slug: enterprise.slug,
        tier: enterprise.tier?.name ?? '',
        avatarUrl: enterprise.avatarUrl ?? null,
        memberCount: enterprise.memberCount,
        teamCount: enterprise.teamCount,
    })),
)
const table = useClientTable({ data: rows, searchFields: ['name', 'slug'] })
/** #633: the picker lists every enterprise the person holds, so its choices are the person's, not one enterprise's. */
const list = useListState('enterprises', table.listFields, { scope: 'person' })
/** The Slug column is Advanced only: the table hides it in Standard without rewriting the person's saved columns. */
const columns: DataTableColumn[] = [
    { key: 'name', label: 'Enterprise', sort: 'name', type: 'text' },
    { key: 'slug', label: 'Slug', sort: 'slug', type: 'text', advancedOnly: true },
    { key: 'tier', label: 'Plan', filterable: false },
    { key: 'counts', label: 'Counts', filterable: false },
]

const creating = ref(false)

function isCurrent(enterprise: MeEnterprise): boolean {
    return enterprise.id === props.me.enterprise.id && props.me.enterpriseChosen !== false
}

async function select(enterpriseId: string): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    try {
        // Even the enterprise already shown must be chosen, or the next load asks again.
        await api.selectEnterprise(enterpriseId)
        reloadIntoEnterprise()
    } catch {
        // #519: one plain sentence in either mode, whatever was thrown; the server's wording is not for the person.
        error.value = 'Could not open this enterprise. Try again.'
        busy.value = false
    }
}

/** A waiting count opens its enterprise on the page that count is acted on, in place of wherever it would land. */
function openOn(enterpriseId: string, path: string): void {
    if (busy.value) return
    rememberPortalDestination(path)
    void select(enterpriseId)
}

onMounted(() => {
    // One enterprise is not a choice: sign in to it and move on.
    const only = enterprises.value[0]
    if (layout.value === 'single' && only) {
        void select(only.id)
    }
})
</script>

<template>
	<main class="relative flex min-h-svh w-full flex-col" data-testid="enterprise-picker">
		<ZhBackdrop intensity="chrome" />
		<header class="relative z-10 flex min-h-12 shrink-0 items-center gap-3 px-4 py-2">
			<img :src="logo" alt="Zero Human" class="size-7" />
			<ZhSystemLabel as="span" class="hidden sm:inline">Zero Human</ZhSystemLabel>
			<div class="ml-auto flex items-center gap-3">
				<ZhSystemLabel v-if="me.user.name" as="span" tone="muted" class="hidden sm:inline">{{ me.user.name }}</ZhSystemLabel>
				<ZhTooltip text="Log out">
					<Button type="button" size="sm" variant="outline" aria-label="Log out" @click="emit('logout')">
						<LogOut />
					</Button>
				</ZhTooltip>
			</div>
		</header>
		<Separator class="relative z-10" />

		<section class="relative z-10 w-full space-y-6 p-4 md:p-8">
			<div class="space-y-1">
				<ZhPageTitle>Choose an enterprise</ZhPageTitle>
				<p class="text-muted-foreground text-sm">
					Pick the company to work in. To come back to this list later, use the “Switch enterprise or log out” button at
					the top right of any page.
				</p>
			</div>
			<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>

			<p v-if="layout === 'single'" class="text-muted-foreground text-sm" role="status">
				{{ enterprises.length ? 'Opening your enterprise…' : 'You do not hold an enterprise yet. Create one below.' }}
			</p>

			<TransitionGroup
				v-else-if="layout === 'grid'"
				tag="div"
				move-class="transition-transform duration-500 ease-in-out motion-reduce:transition-none"
				class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
				data-testid="enterprise-grid"
			>
				<!--
					The card is the kit surface itself; its hover and `aria-current` state come from the panel. A click anywhere
					on it opens the enterprise, and its head is the button that does so from the keyboard. It is not one button,
					because each waiting count inside it is a button of its own, opening the enterprise on that count's page.
				-->
				<ZhPanel
					v-for="enterprise in enterprises"
					:key="enterprise.id"
					as="div"
					interactive
					class="flex cursor-pointer flex-col gap-2 text-left"
					:aria-current="isCurrent(enterprise) ? 'true' : undefined"
					data-testid="enterprise-card"
					@click="select(enterprise.id)"
				>
					<button
						type="button"
						class="flex cursor-pointer items-start justify-between gap-2 text-left disabled:cursor-default"
						:aria-label="`Open ${enterprise.name}`"
						:aria-describedby="billingReasonOf(enterprise.id) ? billingReasonId(enterprise.id) : undefined"
						:disabled="busy"
						data-testid="enterprise-open"
					>
						<span class="flex min-w-0 items-center gap-2">
							<EnterpriseAvatar :enterprise="enterprise" class="size-8 shrink-0" fallback-class="text-xs" />
							<span class="truncate font-medium">{{ enterprise.name }}</span>
						</span>
						<span class="flex shrink-0 items-center gap-1.5">
							<EnterpriseBillingFlag :alert="billing.get(enterprise.id)" />
							<ZhStatusPill v-if="enterprise.tier" tone="plan">{{ enterprise.tier.name }}</ZhStatusPill>
						</span>
						<span v-if="billingReasonOf(enterprise.id)" :id="billingReasonId(enterprise.id)" class="sr-only">{{ billingReasonOf(enterprise.id) }}</span>
					</button>
					<span v-if="showSlug" class="text-muted-foreground text-xs">{{ enterprise.slug }}</span>
					<span v-if="chipsOf(enterprise).length > 0" class="flex flex-wrap items-center gap-1.5" data-testid="enterprise-chips">
						<EnterpriseChips
							:chips="chipsOf(enterprise)"
							:enterprise-name="enterprise.name"
							:disabled="busy"
							@open="path => openOn(enterprise.id, path)"
						/>
					</span>
					<span v-if="isCurrent(enterprise)" class="text-muted-foreground text-xs">Signed in here now</span>
				</ZhPanel>
			</TransitionGroup>

			<ZhPanel v-else flush data-testid="enterprise-table">
				<DataTable
					storage-key="enterprises"
					:columns="columns"
					:loading="busy"
					:list-state="list"
					v-bind="table.tableProps.value"
					@row-click="row => select(String(row.id))"
				>
					<template #cell-name="{ row }">
						<span class="inline-flex items-center gap-2">
							<EnterpriseAvatar :enterprise="{ name: String(row.name), avatarUrl: (row.avatarUrl as string | null) ?? null }" class="size-6" fallback-class="text-xs" />
							<span class="font-medium">{{ row.name }}</span>
						</span>
						<span v-if="isCurrent(row as unknown as MeEnterprise)" class="text-muted-foreground ml-2 text-xs">signed in</span>
					</template>
					<template #cell-tier="{ row, value }">
						<span class="inline-flex items-center gap-1.5">
							<EnterpriseBillingFlag :alert="billing.get(String(row.id))" />
							<ZhStatusPill v-if="value" tone="plan">{{ value }}</ZhStatusPill>
						</span>
					</template>
					<template #cell-counts="{ row }">
						<span class="inline-flex flex-wrap items-center gap-1.5">
							<EnterpriseChips
								:chips="chipsOf(row as unknown as MeEnterprise)"
								:enterprise-name="String(row.name)"
								:disabled="busy"
								@open="path => openOn(String(row.id), path)"
							/>
						</span>
					</template>
				</DataTable>
			</ZhPanel>

			<ZhPanel measure="form" class="space-y-3" data-testid="enterprise-create">
				<div class="flex items-center justify-between gap-3">
					<ZhSystemLabel as="h2">New enterprise</ZhSystemLabel>
					<Button v-if="!creating" type="button" size="sm" variant="outline" @click="creating = true">
						<Plus /> New enterprise
					</Button>
				</div>
				<EnterpriseCreateForm v-if="creating" @cancel="creating = false" />
			</ZhPanel>
		</section>
	</main>
</template>
